import { z } from "zod";
import { FailureSchema, IsoDateTimeSchema } from "./common";
import {
  BlockIdSchema,
  BookIdSchema,
  ChapterIdSchema,
  NodeIdSchema,
  SampleIdSchema,
  VoiceIdSchema,
} from "./ids";
import { JobSummarySchema } from "./jobs";
import { SampleKindSchema, TtsModelSchema } from "./states";

/**
 * What a Sample speaks: a Block of a Chapter with a script, a Manuscript
 * paragraph of a Chapter without one, or a Style Sheet term in the sentence
 * where it first occurs (the Pronunciation Check).
 */
export const SampleSourceSchema = z.discriminatedUnion("kind", [
  z.strictObject({ kind: z.literal("block"), chapterId: ChapterIdSchema, blockId: BlockIdSchema }),
  z.strictObject({ kind: z.literal("node"), chapterId: ChapterIdSchema, nodeId: NodeIdSchema }),
  z.strictObject({
    kind: z.literal("term"),
    termIndex: z.int().nonnegative(),
    styleSheetVersion: z.int().positive(),
  }),
]);
export type SampleSource = z.infer<typeof SampleSourceSchema>;

/** POST /books/:id/samples. `voiceId` is the Voice under comparison; absent means the Narrator, else the language default. */
export const SampleRequestSchema = z.strictObject({
  expectedVoiceRate: z.number().positive().optional(),
  draftText: z.string().trim().min(1).max(20000).optional(),
  source: SampleSourceSchema,
  voiceId: VoiceIdSchema.optional(),
});
export type SampleRequest = z.infer<typeof SampleRequestSchema>;

export const SAMPLE_STATUSES = ["cached", "rendering", "failed"] as const;
export const SampleStatusSchema = z.enum(SAMPLE_STATUSES);
export type SampleStatus = z.infer<typeof SampleStatusSchema>;

/**
 * A Sample. 200 with status cached when the text was heard in this Voice
 * before (replay is free); 202 with status rendering when a sample job was
 * enqueued (poll GET /samples/:id); failed carries the job's failure.
 */
export const SampleSchema = z.strictObject({
  id: SampleIdSchema,
  bookId: BookIdSchema,
  kind: SampleKindSchema,
  status: SampleStatusSchema,
  source: SampleSourceSchema,
  voice: z.strictObject({ id: VoiceIdSchema, name: z.string() }),
  model: TtsModelSchema,
  /** The text as the Author reads it (about one paragraph, cut at a sentence end). */
  text: z.string(),
  /** Characters sent to ElevenLabs (respellings and IPA applied); what the sample cost. */
  characterCount: z.int().nonnegative(),
  durationMs: z.int().nonnegative().nullable(),
  /** Waveform peaks in 0..1 for the player, ~100 values; null until rendered. */
  peaks: z.array(z.number().min(0).max(1)).nullable(),
  failure: FailureSchema.nullable(),
  job: JobSummarySchema.nullable(),
  createdAt: IsoDateTimeSchema,
});
export type Sample = z.infer<typeof SampleSchema>;
